/* eslint-disable @typescript-eslint/no-explicit-any */
/* eslint-disable @typescript-eslint/no-non-null-assertion */
import React from 'react';
import { useI18n } from '@whizz/react-i18n';
import { createGlobalStyle } from 'styled-components';
import type { HTMLAttributes } from 'react';
import { Table } from '../../../components';
import { TableHeader } from './ProgressTable.components';
import { SectionLayout } from '../modules';
import type { TableReportGroupType, TableReportType, TableStudentType } from '../../../mapper/groupTopicReportMapper';
import type { ClassReportType } from '../../types';
import HighlightCard from '../modules/HighlightCard';

type Ref = HTMLDivElement;
type HTMLProps = HTMLAttributes<Ref>;

interface Props extends HTMLProps {
  tableTopicProgress: TableReportType | TableReportGroupType;
  filteredIds: number[] | null;
  dataType?: 'school' | 'class';
  /**
   * Dropdown component.
   */
  dropdown: React.ReactNode;
  /**
   * Class report data.
   */
  classReport?: ClassReportType;
  /**
   * Highlighted element(s) id.
   */
  highlightedIds: number[] | null
  showOthersData: boolean
}

const COLUMN_WIDTHS = {
  'name': 200,
};

// language=SCSS
const GlobalStyle = createGlobalStyle`
  table {
    thead {
      tr:first-child {
        height: 300px;
      }
      tr:nth-child(2) {
        border: 1px solid black;
        border-right: 0;
        border-left: 0;
        font-weight: bold;
      }
    },
    [data-id='footer_maths_age'],
    [data-column='cell_maths_age'],
    [data-id^='footer_'],
    [data-id^='cell_'] {
      text-align: center;
      > div {
        text-align: center;
        width: 100%;
        white-space: nowrap;
      }
    },
    [data-column='cell_name'] {
      font-weight: normal !important;
    },
    [data-id^='col_'] {
      > div {
        flex-direction: column;
        justify-content: flex-end;
        height: 100%;
        @supports (-moz-appearance:none) {
          height: 300px !important;
        }
      }
    },
    [data-id*='_name'] {
      text-align: left;
    },
    [data-id^='col'] {
      width: 100%;
    }
    [data-id='col_name'] {
      min-width: ${COLUMN_WIDTHS.name}px;
    }
`;

const ProgressTable = ({ tableTopicProgress, filteredIds, dropdown, showOthersData, dataType = 'class' }: Props): JSX.Element => {
  const { translate } = useI18n();

  const columns = [
    {
      Header: (<TableHeader title={''} />),

      Cell: ({ row }: { row: any }) =>
        filteredIds?.includes(row.original.id)
          ? (
            <div className={'flex my-sm'}>
              <HighlightCard label={row.values.name} highlight />
            </div>
          )
          : (
            <div className={'flex'}>
              {row.values.name}
            </div>
          )
      ,
      accessor: 'name',
      Footer: translate(`pages.REPORTS.HEADERS.${dataType === 'school' ? 'SCHOOL' : 'CLASS'}_AVERAGE`)!,
      sticky: 'left',
    },
    {
      Header: (<TableHeader title={translate('pages.REPORTS.HEADERS.MATHS_AGE.label')} />),
      accessor: 'mathsAge',
      Footer: tableTopicProgress.mathsAgeAvg,
    },
    // Topics
    ...tableTopicProgress.topics.map((topic) => ({
      Header: (
        <TableHeader title={topic.name} />
      ),
      accessor: `topic${topic.index}`,
      Footer: topic.topicAgeAvg,
    })),
  ];

  const getStyle = (id: number) : Record<string, unknown> => {
    if (filteredIds?.includes(id)) return {
      style: {
        'border':  '1px solid black',
        'borderRight': '0px',
        'borderLeft': '0',
      },
    };

    if (!showOthersData) return {
      style: {
        display: 'none',
      },
    };

    return {};
  };

  const getContent = () => {
    if ('students' in tableTopicProgress) return tableTopicProgress.students;
    if ('groups' in tableTopicProgress) return tableTopicProgress.groups;
    return [];
  };

  return (
    <SectionLayout
      title={translate('pages.REPORTS.SECTIONS.TOPIC_PROGRESS.title')}
      canBeExpanded={false}
      overrideTitle={dropdown}
    >
      <GlobalStyle />
      <div className={'mt-lg'}>
        <Table
          id={'class_report'}
          cols={columns}
          content={getContent()}
          sticky
          footerPosition={showOthersData ? 'top' : null}
          getRowProps={(row) => getStyle((row.original as TableStudentType).id)}
          initialState={{
            sortBy: [
              {
                id: 'name',
                desc: false,
              },
            ],
          }}
        />
      </div>
    </SectionLayout>
  );
};

ProgressTable.displayName = 'ProgressTable';

export default ProgressTable;
